import { SCORING } from './config';
import type { LatLng } from './types';

const DEG = Math.PI / 180;

export const toRadians = (deg: number): number => deg * DEG;
export const toDegrees = (rad: number): number => rad / DEG;

/** Clamp a latitude to [-90, 90]. Non-finite input becomes 0. */
export function clampLat(lat: number): number {
  if (!Number.isFinite(lat)) return 0;
  return Math.min(90, Math.max(-90, lat));
}

/** Clamp a longitude to [-180, 180]. Non-finite input becomes 0. */
export function clampLng(lng: number): number {
  if (!Number.isFinite(lng)) return 0;
  return Math.min(180, Math.max(-180, lng));
}

/** Wrap any longitude into (-180, 180]. */
export function wrapLng(lng: number): number {
  let l = ((lng + 180) % 360) - 180;
  if (l <= -180) l += 360;
  return l;
}

/**
 * Great-circle distance in km using the haversine formula (PRD §4.3).
 * Correct across the antimeridian and at the poles.
 */
export function haversineKm(a: LatLng, b: LatLng): number {
  const φ1 = toRadians(a.lat);
  const φ2 = toRadians(b.lat);
  const dφ = toRadians(b.lat - a.lat);
  const dλ = toRadians(b.lng - a.lng);
  const h =
    Math.sin(dφ / 2) * Math.sin(dφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) * Math.sin(dλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(Math.max(0, 1 - h)));
  return SCORING.earthRadiusKm * c;
}

type XYZ = [number, number, number];

function toUnitVector(p: LatLng): XYZ {
  const φ = toRadians(p.lat);
  const λ = toRadians(p.lng);
  return [Math.cos(φ) * Math.cos(λ), Math.cos(φ) * Math.sin(λ), Math.sin(φ)];
}

function fromUnitVector([x, y, z]: XYZ): LatLng {
  return {
    lat: toDegrees(Math.atan2(z, Math.sqrt(x * x + y * y))),
    lng: toDegrees(Math.atan2(y, x)),
  };
}

/**
 * Points along the great-circle arc from `a` to `b` (inclusive), as [lng, lat] pairs
 * ready for a GeoJSON LineString. Longitudes are unwrapped so the line never jumps
 * across the antimeridian (values may exceed ±180, which MapLibre renders correctly).
 */
export function greatCircleArc(a: LatLng, b: LatLng, segments = 64): [number, number][] {
  const n = Math.max(1, Math.floor(segments));
  const va = toUnitVector(a);
  const vb = toUnitVector(b);
  const dot = Math.min(1, Math.max(-1, va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2]));
  const d = Math.acos(dot);

  const points: [number, number][] = [];
  if (d < 1e-9) {
    // Identical points: a degenerate two-point line.
    return [
      [a.lng, a.lat],
      [b.lng, b.lat],
    ];
  }

  if (Math.PI - d < 1e-6) {
    // Antipodal: infinitely many great circles. Route via the north pole.
    const half = Math.ceil(n / 2);
    const pole = { lat: 90, lng: a.lng };
    const first = greatCircleArc(a, pole, half);
    const second = greatCircleArc({ lat: 90, lng: b.lng }, b, half);
    return unwrap([...first, ...second.slice(1)]);
  }

  const sinD = Math.sin(d);
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const A = Math.sin((1 - f) * d) / sinD;
    const B = Math.sin(f * d) / sinD;
    const p = fromUnitVector([A * va[0] + B * vb[0], A * va[1] + B * vb[1], A * va[2] + B * vb[2]]);
    points.push([p.lng, p.lat]);
  }
  // Snap the endpoints exactly.
  points[0] = [a.lng, a.lat];
  points[n] = [b.lng, b.lat];
  return unwrap(points);
}

/** Make consecutive longitudes continuous (no jumps larger than 180°). */
function unwrap(points: [number, number][]): [number, number][] {
  const out: [number, number][] = [];
  let prev: number | null = null;
  for (const [lng, lat] of points) {
    let l = lng;
    if (prev !== null) {
      while (l - prev > 180) l -= 360;
      while (l - prev < -180) l += 360;
    }
    out.push([l, lat]);
    prev = l;
  }
  return out;
}

/** Midpoint of the great-circle arc, useful for camera framing. */
export function midpoint(a: LatLng, b: LatLng): LatLng {
  const arc = greatCircleArc(a, b, 2);
  const mid = arc[1] ?? [a.lng, a.lat];
  return { lat: mid[1], lng: wrapLng(mid[0]) };
}
